import {
  FlightBlockOrigins,
  FlightBlockResults,
  LegalCopy,
} from '@dynamic-components/isolated-flight-block-container/flight-block.type';
import { Image, Link } from '@shared/index';
import {
  JbFlagCornerPosition,
  JbFlagTheme,
  JbTheme,
  JbThemeEnum,
} from 'jb-component-library';

import { OfferBlockDataType } from './offer-block.type';

export enum TemplateTypes {
  promo = 'promo',
  info = 'info',
}

export enum NewWindow {
  _blank = '_blank',
  _self = '_self',
}

export enum Alignment {
  left = 'left',
  right = 'right',
  center = 'center',
  bottom = 'bottom',
  top = 'top',
}

export enum Theme {
  light = 'light',
  dark = 'dark',
}

export enum ButtonStyle {
  primary = 'primary',
  standard = 'standard',
}

export enum CLButtonStyles {
  primary = 'ctaPrimary',
  secondary = 'ctaSecondary',
  standard = 'standard',
}

// Helper interfaces
export interface OverlayImage {
  cropping: string;
  desktopVPosition: string;
  desktopHPosition: string;
  imageSize: string;
  src: string;
}

export interface promoBlockButton {
  title: string;
  style: ButtonStyle;
  href: string;
  subtitle?: string;
  alt?: string;
  newWindow?: string;
  type?: string;
  children?: Link[];
  image?: Image;
  isRounded?: any;
  isPaired?: any;
  isLoading?: any;
  showChat?: 'false' | 'true';
  cookieConsent?: 'false' | 'true';
}

export type OfferSettings = {
  showImage: 'false' | 'true'; // should be 'true' or 'false'
  legalCopy: LegalCopy[];
  origins: FlightBlockOrigins[];
  results: FlightBlockResults[];
  offerBlock: OfferBlockDataType[];
};

// Promo Block Data Type
export interface PromoBlockDataType {
  fullBleed?: 'false' | 'true';
  // logo
  image?: Image[];
  flag?: [
    {
      title: string;
      theme?: Theme;
      roundedCorner?: string;
    },
  ];
  title: string;
  description: string;
  legalCopy?: [
    {
      content: string;
      title: string;
    },
  ];
  // styling
  desktopVPosition: string;
  opacity: string;
  bgColor: string;
  content: string;
  size: string;
  theme: JbTheme;
  color: string;
  contentAlignment: Alignment;
  desktopHPosition: string;
  background: [
    {
      repeat: string;
      src: string;
    },
  ];
  overlayImage?: OverlayImage[];
  promoCard: {
    offer?: OfferBlockDataType[];
    // can be either a one button or two button layout, with different structures
    oneButton?: (
      | { offer: [OfferBlockDataType] }
      | { button: [promoBlockButton] }
    )[];
    twoButton?: [
      { button: [promoBlockButton]; offer: [OfferBlockDataType] },
      { button: [promoBlockButton]; offer: [OfferBlockDataType] },
    ];
  }[];
  dynamicOffer?: {
    useImageAs?: string;
    overlayImage?: string;
    offerSettings: OfferSettings;
  }[];
}

/**
 * The Offer Block comes in from the CMS and represents the data we want to put into the little
 * blocks in the middle of the promo card, they contain information such as the price
 */

export type PromoBlockFlagSettings = {
  flagTheme: JbFlagTheme;
  flagAlignment: JbFlagCornerPosition;
  flagTitle?: string;
};

export type overlayImageSettings = {
  cropping: string;
  desktopVPosition: string;
  desktopHPosition: string;
  imageSize: string;
};

/** PromoCard structure after CMS promoCard parse */
export type ViewPromoCard = {
  offer?: OfferBlockDataType[];
  button?: promoBlockButton[];
  externalButtons?: any[];
};

/** PromoBlock model used on the UI after CMS data has been parsed */
export type ViewPromoBlock = Omit<PromoBlockDataType, 'promoCard'> & {
  promoCard: ViewPromoCard;
};

/**
 * This is the transformed CMS data required to successfully render all the variations of the promo block
 */
export type PromoBlockView = {
  /**
   * In the case of there being a single promo block button with a link, we want to make the entire promo block
   * clickable and navigate with a link, this is null in the case that the entire block should NOT be clickable
   */
  fullPromoBlockLink: string | null;
  /**
   * The jbFlag is a small CL element below the title area of the promo block, that changes
   * theme and alignment depending on a few different variables
   */
  flagSettings: PromoBlockFlagSettings;
  /**
   * An array of offer blocks, can be anywhere from 0 to 2 offer blocks
   */
  offerBlocks: OfferBlockDataType[];
  /**
   * Defines offer block's theme according to the promo block's theme
   */
  offerBlockTheme: JbThemeEnum;

  promoBlock: ViewPromoBlock;
};

export type PromoBlockResolverResult = {
  data: PromoBlockView[];
};

export type PromoBlockImageSetup = {
  url: string;
  isForeground: boolean;
  isBackground: boolean;
};
